import React, { createContext, useContext, useEffect, useState } from "react"
import { useSelector } from "react-redux"

const initialState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}) {
  const user = useSelector((state) => state.auth?.user)
  
  // 1. Initialize from localStorage
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem(storageKey) || defaultTheme
  })

  // 2. Sync Redux Preference -> Local State & Storage (Triggered on login/refresh)
  useEffect(() => {
    if (user?.preferences?.theme) {
      const dbTheme = user.preferences.theme;
      if (dbTheme !== theme) {
        localStorage.setItem(storageKey, dbTheme);
        setTheme(dbTheme);
      }
    }
  }, [user?.preferences?.theme, theme, storageKey])

  // 3. Apply DOM classes
  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (newTheme) => {
      localStorage.setItem(storageKey, newTheme)
      setTheme(newTheme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)
  if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider")
  return context
}
